import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { examApi, unlockCost, type ExamListResponse, type ExamCard, type AccessStatus } from '../../services/examApi';
import { useAccountStore } from '../../stores/accountStore';
import type { TabKey } from '../../components/layout/TopTabs';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

/** 访问状态徽标 */
const StatusBadge: React.FC<{ status: AccessStatus; cost: number }> = ({ status, cost }) => {
  const map: Record<AccessStatus, { text: string; cls: string }> = {
    subscribed: { text: 'Subscribed', cls: 'bg-primary-100 text-primary-700' },
    unlocked: { text: 'Unlocked', cls: 'bg-primary-100 text-primary-700' },
    unlockable: { text: `Unlock (${cost} credit${cost > 1 ? 's' : ''})`, cls: 'bg-amber-100 text-amber-700' },
    open: { text: 'Free', cls: 'bg-gray-100 text-gray-600' },
  };
  const m = map[status];
  return <span className={`text-xs px-2 py-0.5 rounded-full ${m.cls}`}>{m.text}</span>;
};

const ExamCardItem: React.FC<{
  card: ExamCard;
  busy: boolean;
  examType: string;
  onStart: (card: ExamCard) => void;
  onViewReport: (card: ExamCard) => void;
}> = ({ card, busy, examType, onStart, onViewReport }) => {
  const done = !!card.lastAttempt;
  const canView = done && card.lastAttempt?.hasReport;
  const cost = unlockCost(examType, card.subject); // 按 exam_type + subject 计算
  return (
    <div className="bg-white rounded-xl border border-primary-100 hover:border-primary-300 hover:shadow-md transition p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-gray-800 leading-snug">{card.title}</h4>
        <StatusBadge status={card.accessStatus} cost={cost} />
      </div>
      <div className="text-xs text-gray-400 flex gap-2">
        {card.year && <span>{card.year}</span>}
        {card.month && <span>{card.month}</span>}
        {card.region && <span>{card.region}</span>}
      </div>
      <div className="flex gap-2 mt-auto pt-1">
        {done ? (
          <>
            <button
              disabled={busy || !canView}
              onClick={() => onViewReport(card)}
              className="btn btn-primary text-sm py-1.5 px-3 disabled:opacity-50"
            >
              View Report
            </button>
            <button
              disabled={busy}
              onClick={() => onStart(card)}
              className="btn text-sm py-1.5 px-3 border border-primary-200 text-primary-700 hover:bg-primary-50 disabled:opacity-50"
            >
              Retake
            </button>
          </>
        ) : (
          <button
            disabled={busy}
            onClick={() => onStart(card)}
            className="btn btn-primary text-sm py-1.5 px-3 disabled:opacity-50"
          >
            {card.accessStatus === 'unlockable' ? `Unlock (${cost} credit${cost > 1 ? 's' : ''})` : 'Start'}
          </button>
        )}
      </div>
    </div>
  );
};

interface ExamListPageProps {
  tab: TabKey;
}

export const ExamListPage: React.FC<ExamListPageProps> = ({ tab }) => {
  const navigate = useNavigate();
  const fetchAccount = useAccountStore((s) => s.fetchAccount);
  const [data, setData] = useState<ExamListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState<number | 'all'>('all');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [unlockTarget, setUnlockTarget] = useState<ExamCard | null>(null); // 待确认解锁的试卷
  const [toast, setToast] = useState<{ text: string; kind: 'error' | 'success' } | null>(null);

  // 待确认解锁试卷的扣费额（按 exam_type=tab + 该卡片 subject）
  const cost = unlockTarget ? unlockCost(tab, unlockTarget.subject) : 0;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setYearFilter('all');
    examApi
      .list(tab)
      .then((res) => {
        if (mounted) { setData(res); setLoading(false); }
      })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [tab]);

  // 提示自动消失
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  // 按年份筛选后的分组
  const groups = useMemo(() => {
    if (!data) return [];
    if (yearFilter === 'all') return data.groups;
    return data.groups
      .map((g) => ({ ...g, items: g.items.filter((i) => i.year === yearFilter) }))
      .filter((g) => g.items.length > 0);
  }, [data, yearFilter]);

  const handleStart = (card: ExamCard) => {
    // 可做的直接进；unlockable 需先弹窗确认再用 credit 解锁
    if (card.accessStatus === 'unlockable') {
      setUnlockTarget(card);
      return;
    }
    navigate(`/exam/${card.id}`);
  };

  const confirmUnlock = async () => {
    if (!unlockTarget) return;
    const card = unlockTarget;
    setBusyId(card.id);
    try {
      await examApi.unlock(card.id);
      await fetchAccount(); // 刷新 credit 余额
      setUnlockTarget(null);
      navigate(`/exam/${card.id}`);
    } catch (e: any) {
      setUnlockTarget(null);
      setToast({ text: e?.message || 'Failed to unlock exam', kind: 'error' });
    } finally {
      setBusyId(null);
    }
  };

  const handleViewReport = (card: ExamCard) => {
    if (card.lastAttempt?.attemptId) navigate(`/report/${card.lastAttempt.attemptId}`);
  };

  if (loading) return <div className="p-8 text-gray-400">Loading…</div>;
  if (!data || data.groups.length === 0)
    return <div className="p-8 text-gray-400">No exams available.</div>;

  return (
    <div className="p-6 space-y-6">
      {/* 年份筛选 */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm text-gray-500 mr-1">Year:</span>
        <button
          onClick={() => setYearFilter('all')}
          className={`px-3 py-1 rounded-full text-sm transition ${
            yearFilter === 'all' ? 'bg-primary-500 text-white' : 'bg-white border border-primary-100 text-gray-600 hover:border-primary-300'
          }`}
        >
          All
        </button>
        {data.years.map((y) => (
          <button
            key={y}
            onClick={() => setYearFilter(y)}
            className={`px-3 py-1 rounded-full text-sm transition ${
              yearFilter === y ? 'bg-primary-500 text-white' : 'bg-white border border-primary-100 text-gray-600 hover:border-primary-300'
            }`}
          >
            {y}
          </button>
        ))}
      </div>

      {/* 按学科分组 */}
      {groups.map((g) => (
        <section key={g.subject} className="space-y-3">
          <h3 className="text-base font-semibold text-gray-700">{g.subject}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {g.items.map((card) => (
              <ExamCardItem
                key={card.id}
                card={card}
                busy={busyId === card.id}
                examType={tab}
                onStart={handleStart}
                onViewReport={handleViewReport}
              />
            ))}
          </div>
        </section>
      ))}

      {/* 解锁确认弹窗 */}
      <ConfirmDialog
        open={!!unlockTarget}
        title="Unlock this exam?"
        message={
          <>
            Unlocking <span className="font-medium text-gray-800">{unlockTarget?.title}</span> will use{' '}
            <span className="font-medium text-primary-700">{cost} credit{cost > 1 ? 's' : ''}</span>. This gives you full access to this exam.
          </>
        }
        confirmText={`Unlock (${cost} credit${cost > 1 ? 's' : ''})`}
        cancelText="Cancel"
        busy={!!unlockTarget && busyId === unlockTarget.id}
        onConfirm={confirmUnlock}
        onCancel={() => setUnlockTarget(null)}
      />

      {/* 轻量提示 */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div
            className={`px-4 py-2 rounded-lg shadow-lg text-sm ${
              toast.kind === 'error' ? 'bg-red-600 text-white' : 'bg-gray-900 text-white'
            }`}
          >
            {toast.text}
          </div>
        </div>
      )}
    </div>
  );
};
